<script setup lang="ts" generic="MODE extends 'edit' | 'create'">
import { toTypedSchema } from "@vee-validate/zod";
import { brandString } from "@voltalis/frontend-utils";
import { last } from "es-toolkit";
import { first } from "es-toolkit/compat";
import { useForm } from "vee-validate";
import { watch, computed } from "vue";
import { z } from "zod";

import { SiteContext } from "@/app/control/programming/contexts/site-context";
import {
  IInsertRange,
  IRangeFormParams,
  TEditRangeFormParams,
  TInsertRangeFormParams,
  TSelectedRange,
} from "@/components/programming/weekly-program/daily-program.type";
import { RANGE_MIN_THRESHOLD, THeaterSubProgramRange } from "@/components/programming/weekly-program/site-program.type";
import { useContext } from "@/composables/context";
import { useToaster } from "@/composables/layout/use-toaster";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { planningMetaModeSchema } from "@/data/api/kili/sites/types/planning.type";
import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import {
  EHeaterProgramMetaMode,
  EOnProgramMetaMode,
  TProgramMetaMode,
  ECommonProgramMetaMode,
  SubprogramRange,
} from "@/domains/v4b/program/subprogram.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { cn } from "@/utils/libs/tailwind-utils";
import { pad } from "@/utils/number";
import { isDefined } from "@/utils/object";
import { getMetaModeGroupInstructions } from "@/utils/programs/program-utils";
import {
  addMinutes,
  substractMinutes,
  getHours,
  getMinutes,
  MAX_DATE_TIME,
  MAX_STRING_TIME,
  dateMinutesDiff,
  MIN_STRING_TIME,
  getDayTimeSliderValue,
  getDayHourFromSliderValue,
  getDayMinutesFromSliderValue,
} from "@/utils/time";
import { getModeEquivalentForNotFR } from "@/utils/translation-utils";

import Checkbox from "primevue/checkbox";
import DatePicker from "primevue/datepicker";
import InputNumber from "primevue/inputnumber";
import Slider from "primevue/slider";

import ProgramModeIcon from "@/components/programming/program-mode-icon.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import BaseDialog from "@/components/ui/dialog/base-dialog.vue";

interface IProps {
  selectedRange: MODE extends "edit" ? TSelectedRange | null : IInsertRange | null;
  applianceCategory: EProgramApplianceCategory;
  mode?: MODE;
}

interface IEmits {
  (e: "edit", params: TEditRangeFormParams): void;
  (e: "insert", params: TInsertRangeFormParams): void;
}

const props = defineProps<IProps>();
const emits = defineEmits<IEmits>();
const visible = defineModel<boolean>();

const { t, k, rawT } = useTypedIntl();
const toast = useToaster();
const state = useContext(SiteContext);

const availableInstructions = computed<Exclude<TProgramMetaMode, "PROGRAMMING">[]>(() => {
  return getMetaModeGroupInstructions(props.applianceCategory);
});

const isRangeUnique = computed<boolean>(() => {
  return (
    props.selectedRange?.range.startTime === MIN_STRING_TIME && props.selectedRange?.range.endTime === MAX_STRING_TIME
  );
});

const disabledDatePickerTooltipContent = computed<string>(() => {
  return props.mode === "edit" && isRangeUnique.value ? t(k.control.programming.range.error.emptyRange) : "";
});

const rangeFormSchema = z.object({
  metaMode: planningMetaModeSchema,
  temperature: z.number().nullable(),
  startTime: z.date(),
  endTime: z.date(),
  applyOnBusinessDays: z.boolean(),
  applyOnWeekend: z.boolean(),
});

const { handleSubmit, defineField, setFieldValue, resetForm, errors } = useForm({
  validationSchema: toTypedSchema(rangeFormSchema),
  initialValues: {
    metaMode: undefined,
    temperature: null,
    startTime: undefined,
    endTime: undefined,
    applyOnBusinessDays: false,
    applyOnWeekend: false,
  },
});

const [metaMode] = defineField("metaMode");
const [temperature] = defineField("temperature");
const [startTime] = defineField("startTime");
const [endTime] = defineField("endTime");
const [applyOnBusinessDays] = defineField("applyOnBusinessDays");
const [applyOnWeekend] = defineField("applyOnWeekend");

const sliderRange = computed<number[]>({
  get() {
    const sliderStart = isDefined(startTime.value) ? getDayTimeSliderValue(startTime.value) : 0;
    const sliderEnd = isDefined(endTime.value) ? getDayTimeSliderValue(endTime.value) : 100;
    return [sliderStart, sliderEnd];
  },
  set(value: number[]) {
    const sliderStart = first(value);
    const sliderEnd = last(value);

    if (!isDefined(sliderStart) || !isDefined(sliderEnd)) {
      throw new Error("Slider should have defined range values");
    }

    const newStartTime = new Date();
    newStartTime.setHours(getDayHourFromSliderValue(sliderStart), getDayMinutesFromSliderValue(sliderStart));

    startTime.value = newStartTime;

    const newEndTime = new Date();
    newEndTime.setHours(getDayHourFromSliderValue(sliderEnd), getDayMinutesFromSliderValue(sliderEnd));

    endTime.value = newEndTime;
  },
});

const submit = handleSubmit((values) => {
  if (values.endTime < values.startTime) {
    toast.add({ severity: "error", detail: t(k.control.programming.range.error.endInferiorToStart) });
    return;
  }
  if (dateMinutesDiff(values.startTime, values.endTime) < RANGE_MIN_THRESHOLD) {
    toast.add({ severity: "error", detail: t(k.control.programming.range.error.minThreshold) });
    return;
  }

  if (!props.selectedRange) return;

  const start = values.startTime;
  const end = values.endTime;

  const newSelectedRange: TSelectedRange | IInsertRange = {
    ...props.selectedRange,
    range: {
      ...values,
      startTime: brandString(`${pad(start.getHours().toString())}:${pad(start.getMinutes().toString())}`),
      endTime: brandString(`${pad(end.getHours().toString())}:${pad(end.getMinutes().toString())}`),
      temperature: values.metaMode === ECommonProgramMetaMode.TEMP ? values.temperature : null,
    } satisfies SubprogramRange,
  };

  const formParam: IRangeFormParams = {
    applyOnBusinessDays: values.applyOnBusinessDays,
    applyOnWeekend: values.applyOnWeekend,
  };

  if (props.mode === "edit") {
    emits("edit", {
      range: newSelectedRange as TSelectedRange,
      params: formParam,
    });
  } else {
    emits("insert", {
      range: newSelectedRange as IInsertRange,
      params: formParam,
    });
  }

  visible.value = false;
});

watch(
  () => [props.selectedRange, props.mode],
  ([newValues, newMode]) => {
    resetForm();

    if (!newValues || !newMode) {
      return;
    }

    if (newValues && props.mode === "edit") {
      const newRange = (newValues as TSelectedRange).range;

      setFieldValue("metaMode", newRange.metaMode);

      const newStart = new Date();
      const newEnd = new Date();

      newStart.setHours(getHours(newRange.startTime), getMinutes(newRange.startTime));
      newEnd.setHours(getHours(newRange.endTime), getMinutes(newRange.endTime));

      startTime.value = newStart;
      endTime.value = newEnd;

      if (props.applianceCategory === "HEATER") {
        setFieldValue("temperature", (newRange as THeaterSubProgramRange).temperature ?? null);
      }
    } else if (newValues && props.mode === "create") {
      const position = (newValues as IInsertRange).position;

      const newStart = new Date();
      const newEnd = new Date();

      if (position === "start") {
        const newRange = (newValues as IInsertRange).range;
        const newEndTimeWithThreshold: string = addMinutes(newRange.startTime, RANGE_MIN_THRESHOLD);

        newStart.setHours(getHours(newRange.startTime), getMinutes(newRange.startTime));
        newEnd.setHours(getHours(newEndTimeWithThreshold), getMinutes(newEndTimeWithThreshold));

        startTime.value = newStart;
        endTime.value = newEnd;
      } else {
        const newRange = (newValues as IInsertRange).range;
        let newEndTimeWithThreshold: string;

        if (newRange.endTime > substractMinutes(MAX_STRING_TIME, RANGE_MIN_THRESHOLD)) {
          newEndTimeWithThreshold = MAX_STRING_TIME;
        } else {
          newEndTimeWithThreshold = addMinutes(newRange.endTime, RANGE_MIN_THRESHOLD);
        }

        newStart.setHours(getHours(newRange.endTime), getMinutes(newRange.endTime));
        newEnd.setHours(getHours(newEndTimeWithThreshold), getMinutes(newEndTimeWithThreshold));

        startTime.value = newStart;
        endTime.value = newEnd;
      }
    }
  },
  { immediate: true, deep: true },
);
</script>

<template>
  <BaseDialog
    v-model:visible="visible"
    class="program-form-dialog"
    :data-test-id="DATA_TEST_IDS.components.programFormDialog.component"
    modal
    size="medium"
  >
    <template #header>
      <div class="flex flex-col">
        <h4 v-if="props.mode === 'edit' && props.selectedRange">
          {{ t(k.control.programming.range.edition) }}
        </h4>
        <h4 v-else>
          {{ t(k.control.programming.range.addition) }}
        </h4>
        <span class="text-text-secondary">{{ t(k.control.programming.range.subtitle) }}</span>
      </div>
    </template>

    <div class="flex flex-col gap-10">
      <div class="flex flex-col gap-[18px]">
        <Slider
          v-model="sliderRange"
          class="w-full"
          :data-test-id="DATA_TEST_IDS.components.programFormDialog.slider"
          range
        />

        <div class="flex gap-8">
          <section class="flex flex-col gap-[6px]">
            <label
              for="startTime"
              class="text-sm font-medium"
              :data-test-id="DATA_TEST_IDS.components.programFormDialog.startTime.label"
            >
              {{ t(k.control.programming.range.startTime) }}
            </label>
            <DatePicker
              id="startTime"
              v-model="startTime"
              v-tooltip.left="disabledDatePickerTooltipContent"
              :data-test-id="DATA_TEST_IDS.components.programFormDialog.startTime.datePicker"
              :disabled="props.mode === 'edit' && isRangeUnique"
              fluid
              icon-display="input"
              :invalid="!!errors.startTime"
              :max-date="MAX_DATE_TIME"
              show-icon
              time-only
            >
              <template #inputicon="slotProps">
                <i
                  class="pi pi-clock"
                  @click="slotProps.clickCallback"
                />
              </template>
            </DatePicker>
            <small
              v-if="errors.startTime"
              class="text-red-500"
              :data-test-id="DATA_TEST_IDS.components.programFormDialog.startTime.error"
            >
              {{ rawT(`veeValidate.messages.${errors.startTime}`) }}
            </small>
          </section>

          <section class="flex flex-col gap-[6px]">
            <label
              for="endTime"
              class="text-sm font-medium"
              :data-test-id="DATA_TEST_IDS.components.programFormDialog.endTime.label"
            >
              {{ t(k.control.programming.range.endTime) }}
            </label>
            <DatePicker
              id="endTime"
              v-model="endTime"
              v-tooltip.left="disabledDatePickerTooltipContent"
              :data-test-id="DATA_TEST_IDS.components.programFormDialog.endTime.datePicker"
              :disabled="props.mode === 'edit' && isRangeUnique"
              fluid
              icon-display="input"
              :invalid="!!errors.endTime"
              show-icon
              time-only
            >
              <template #inputicon="slotProps">
                <i
                  class="pi pi-clock"
                  @click="slotProps.clickCallback"
                />
              </template>
            </DatePicker>
            <small
              v-if="errors.endTime"
              class="text-red-500"
              :data-test-id="DATA_TEST_IDS.components.programFormDialog.endTime.error"
            >
              {{ rawT(`veeValidate.messages.${errors.endTime}`) }}
            </small>
          </section>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <span class="font-medium">
          {{ t(k.control.programming.range.mode) }}
        </span>

        <div class="flex flex-col gap-2">
          <div class="flex flex-row gap-4">
            <template
              v-for="(programMetaMode, _index) in availableInstructions"
              :key="_index"
            >
              <div
                v-tooltip.top="getModeEquivalentForNotFR(programMetaMode, state.siteDetails?.address)"
                :class="
                  cn('border-2 border-transparent h-[42px] p-3 rounded-full flex items-center justify-center gap-2', {
                    'bg-programming-comfort-light text-programming-comfort-foreground':
                      programMetaMode === EHeaterProgramMetaMode.COMFORT,
                    'bg-programming-eco-light text-programming-eco-foreground':
                      programMetaMode === EHeaterProgramMetaMode.ECO,
                    'bg-programming-temperature-light text-programming-temperature-foreground':
                      programMetaMode === ECommonProgramMetaMode.TEMP,
                    'bg-programming-no-frost-light text-programming-no-frost-foreground':
                      programMetaMode === EHeaterProgramMetaMode.NO_FROST,
                    'bg-inverse text-programming-stop-foreground': programMetaMode === ECommonProgramMetaMode.OFF,
                    'bg-programming-programming text-programming-programming-foreground':
                      programMetaMode === EOnProgramMetaMode.ON,
                    'border-brand': programMetaMode === metaMode,
                  })
                "
                :data-test-id="DATA_TEST_IDS.components.programFormDialog.modeItem"
                :data-program-mode="programMetaMode"
                role="button"
                tabindex="0"
                @click="setFieldValue('metaMode', programMetaMode)"
                @keydown.enter="setFieldValue('metaMode', programMetaMode)"
              >
                <ProgramModeIcon :mode="programMetaMode" />
                <span>
                  {{ t(k.global.program.metaMode[programMetaMode]) }}
                </span>
              </div>
            </template>
          </div>
          <small
            v-if="errors.metaMode"
            class="text-red-500"
            :data-test-id="DATA_TEST_IDS.components.programFormDialog.modeError"
          >
            {{ t(k.control.programming.range.error.mandatoryMode) }}
          </small>
        </div>
      </div>

      <section
        v-if="metaMode === ECommonProgramMetaMode.TEMP"
        class="flex flex-col gap-[6px]"
      >
        <label
          for="temperature"
          class="text-sm font-medium"
          :data-test-id="DATA_TEST_IDS.components.programFormDialog.temperature.label"
          >{{ t(k.control.programming.range.temperature) }}</label
        >
        <InputNumber
          id="temperature"
          v-model="temperature"
          :data-test-id="DATA_TEST_IDS.components.programFormDialog.temperature.input"
          @value-change="setFieldValue('temperature', $event)"
        />
      </section>

      <div class="flex flex-col gap-4">
        <span class="font-medium">
          {{ t(k.control.programming.range.multipleApplications) }}
        </span>

        <div class="flex flex-col gap-4">
          <section class="flex gap-2">
            <Checkbox
              v-model="applyOnBusinessDays"
              binary
              :data-test-id="DATA_TEST_IDS.components.programFormDialog.applyOnBusinessDays.checkbox"
              inputId="applyOnBusinessDays"
            />

            <label
              for="applyOnBusinessDays"
              class="cursor-pointer"
              :data-test-id="DATA_TEST_IDS.components.programFormDialog.applyOnBusinessDays.label"
            >
              {{ t(k.control.programming.range.applyOnBusinessDays) }}</label
            >
          </section>

          <section class="flex gap-2">
            <Checkbox
              v-model="applyOnWeekend"
              binary
              :data-test-id="DATA_TEST_IDS.components.programFormDialog.applyOnWeekend.checkbox"
              inputId="applyOnWeekend"
            />

            <label
              for="applyOnWeekend"
              class="cursor-pointer"
              :data-test-id="DATA_TEST_IDS.components.programFormDialog.applyOnWeekend.label"
            >
              {{ t(k.control.programming.range.applyOnWeekEnd) }}</label
            >
          </section>
        </div>
      </div>
    </div>

    <template #footer>
      <HospitalityButton
        :data-test-id="DATA_TEST_IDS.components.programFormDialog.cancelBtn"
        :label="t(k.global.misc.cancel)"
        severity="secondary"
        @click="visible = false"
      />
      <HospitalityButton
        :data-test-id="DATA_TEST_IDS.components.programFormDialog.submitBtn"
        :disabled="metaMode === ECommonProgramMetaMode.TEMP && !temperature"
        :label="t(k.global.misc.confirm)"
        @click="submit"
      />
    </template>
  </BaseDialog>
</template>

<style lang="scss">
.program-form-dialog {
  .p-dialog-header {
    @apply border-b;
  }

  .p-dialog-content {
    overflow: visible;
  }

  .p-dialog-footer {
    @apply border-t;
  }
}
</style>
